/**
 * ChoreDetail - the full page for one chore
 * -----------------------------------------
 * What you see depends on who you are:
 *   - A student who has not applied:  the Apply button
 *   - A student who has applied:      their application status
 *   - The accepted student:           the address, a Cancel button, and a
 *                                     button to message the poster
 *   - The poster:                     a link through to manage applications,
 *                                     and a button to message the student
 *   - After completion:               the review form, then the reviews
 *
 * THE MESSAGE BUTTON IS HERE BECAUSE IT WAS NOWHERE ELSE.
 * Added 16 September 2026, after the owner reported that a backup promoted to
 * the job "can't text the poster". They could - the server always let the
 * booked student and the poster talk - but the only way in was the speech
 * bubble in the navbar, and nothing on the job itself said a conversation
 * existed. The person with the job in front of them is the one who needs it.
 */

import { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  MapPin,
  Calendar,
  Clock,
  Tag,
  ArrowLeft,
  AlertTriangle,
  CheckCircle2,
  CalendarClock,
  Users,
  Home,
  MessageSquare,
  HelpCircle,
} from 'lucide-react';

import { choreApi, applicationApi, reviewApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { money, dateTime, travel, countdown } from '../utils/format';
import { choreWhenShort } from '../utils/schedule';
import Badge, { StatusBadge, ApplicationBadge } from '../components/Badge';
import StarRating from '../components/StarRating';
import Avatar from '../components/Avatar';
import Loader from '../components/Loader';
import Modal from '../components/Modal';
import ReviewForm from '../components/ReviewForm';
import ReviewCard from '../components/ReviewCard';
import ReportButton from '../components/ReportButton';
import BackLink from '../components/BackLink';

import { useI18n } from '../hooks/useI18n';

export default function ChoreDetail() {
  const { t } = useI18n();
  const { id } = useParams();
  const { user, isStudent, isPoster } = useAuth();

  const [chore, setChore] = useState(null);
  const [myApplication, setMyApplication] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [applyOpen, setApplyOpen] = useState(false);
  const [applyMessage, setApplyMessage] = useState('');
  const [cancelOpen, setCancelOpen] = useState(false);
  // Only asked for when the job starts within 24 hours - see below.
  const [cancelReason, setCancelReason] = useState('');
  const [busy, setBusy] = useState(false);

  // The single day somebody is about to drop out of a multi-day booking, and
  // the reason they give for it. Holding the whole visit rather than its id so
  // the dialog can name the actual date it is about to cancel.
  const [droppingVisit, setDroppingVisit] = useState(null);
  const [dropReason, setDropReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await choreApi.get(id);
      setChore(data.chore);
      setMyApplication(data.myApplication);

      // Reviews name the people involved, so they need a login. A logged-out
      // visitor still sees the chore itself - just without the reviews, and
      // without the page failing.
      if (data.chore.status === 'completed' && user) {
        try {
          const { reviews: list } = await reviewApi.forChore(id);
          setReviews(list);
        } catch {
          setReviews([]);
        }
      } else {
        setReviews([]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id, user]);

  useEffect(() => {
    load();
  }, [load]);

  // ---- Actions -------------------------------------------------------------

  /**
   * Drop one day and leave the rest of the booking standing.
   *
   * The other side is told by the server, so there is nothing to do here but
   * reload - the list of dates, the count and the total all move together and
   * are all worked out server-side.
   */
  const handleDropVisit = async () => {
    if (!droppingVisit) return;
    setBusy(true);
    setError('');
    try {
      await choreApi.cancelVisit(id, droppingVisit._id, dropReason);
      setDroppingVisit(null);
      setDropReason('');
      setNotice(t('choreDetail.dayCancelled'));
      await load();
    } catch (err) {
      setError(err.message);
      setDroppingVisit(null);
    } finally {
      setBusy(false);
    }
  };

  const handleApply = async () => {
    setBusy(true);
    setError('');
    try {
      await choreApi.apply(id, applyMessage);
      setApplyOpen(false);
      setApplyMessage('');
      setNotice(t('choreDetail.applicationSent'));
      await load();
    } catch (err) {
      setError(err.message);
      setApplyOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const handleCancelJob = async () => {
    setBusy(true);
    setError('');
    try {
      const result = await applicationApi.cancelJob(myApplication._id, cancelReason.trim());
      setCancelOpen(false);
      setNotice(
        result.promotedBackup
          ? `You have been removed from this chore. ${result.promotedBackup.fullName} was the backup and has taken it on.`
          : t('choreDetail.removedNotice')
      );
      await load();
    } catch (err) {
      setError(err.message);
      setCancelOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const handleWithdraw = async () => {
    setBusy(true);
    try {
      await applicationApi.withdraw(myApplication._id);
      setNotice(t('choreDetail.applicationWithdrawn'));
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  // ---- Render --------------------------------------------------------------

  if (loading) return <Loader label={t('choreDetail.loading')} />;

  if (!chore) {
    return (
      <div className="container section">
        <div className="alert alert-error">{error || t('choreDetail.notFound')}</div>
        <Link to="/marketplace" className="btn btn-outline btn-inline" style={{ marginTop: 'var(--space-sm)' }}>
          <ArrowLeft size={16} /> {t('choreDetail.backToMarketplace')}
        </Link>
      </div>
    );
  }

  const poster = chore.postedBy;
  const isOwner = user && poster && String(user._id) === String(poster._id);
  const isAssigned = user && chore.assignedStudent && String(user._id) === String(chore.assignedStudent._id);
  const isBackup = user && chore.backupStudent && String(user._id) === String(chore.backupStudent._id);

  // A chore with somebody booked but nobody standing by is still worth
  // applying for - as the backup. If the first student drops out more than 24
  // hours ahead, the backup is promoted automatically.
  const takingBackups = chore.status === 'assigned' && !chore.backupStudent;

  const canApply =
    isStudent &&
    !isAssigned &&
    (chore.status === 'open' || takingBackups) &&
    (!myApplication || myApplication.status === 'withdrawn');

  /**
   * THE POSTER MOVED THE DATES AFTER THIS STUDENT AGREED.
   *
   * Worked out by the server, not here - routes/chores.js sends it on the
   * application. Doing the comparison in the page as well is how the button
   * somebody sees and the rule the server applies end up disagreeing, and
   * the way it would disagree is a button that refuses when pressed.
   */
  const freeCancellation = Boolean(myApplication?.freeCancellation);

  /**
   * Inside 24 hours of the start.
   *
   * This used to mean "cannot cancel". Since 25 August 2026 it means "can
   * cancel, but must say why" - see the cancel route in
   * backend/routes/applications.js for why refusing outright was worse for
   * everybody, including the poster.
   *
   * The free-cancellation case is excluded: somebody whose dates were moved by
   * the poster owes nobody an explanation.
   */
  const withinCancelWindow =
    !freeCancellation && chore.hoursUntilStart !== null && chore.hoursUntilStart < 24;

  const needsCancelReason = withinCancelWindow && myApplication?.status === 'accepted';
  // Matches MIN_LATE_CANCEL_REASON on the server, which is what enforces it.
  const cancelReasonTooShort = cancelReason.trim().length < 15;

  // Whose review am I being asked for?
  const canReview =
    chore.status === 'completed' &&
    ((isOwner && !chore.reviewedByPoster) || (isAssigned && !chore.reviewedByStudent));

  const reviewSubject = isOwner ? chore.assignedStudent : poster;

  /**
   * Who may open the conversation from here: the poster and the student doing
   * the job, once somebody is booked - the same two people, at the same
   * moments, that backend/routes/messages.js lets in. A backup is not one of
   * them until they are promoted, which is what makes the conversation theirs.
   */
  const canMessage =
    (isOwner || isAssigned) &&
    Boolean(chore.assignedStudent) &&
    ['assigned', 'completed'].includes(chore.status);
  const messageName = isOwner ? chore.assignedStudent?.firstName : poster?.firstName;

  /**
   * ASK ABOUT THE JOB BEFORE APPLYING FOR IT.
   *
   * Added 24 September 2026, at the owner's request: somebody should be able
   * to find out whether the mower is petrol or electric without committing to
   * the afternoon first.
   *
   * Only a student, only while the chore is still taking applications, and
   * only the student can open it - an adult can never start a conversation
   * with a child here or anywhere else. The server enforces every one of
   * those; this decides whether to draw the button. See
   * backend/routes/messages.js.
   */
  const canAskAboutJob = isStudent && !isOwner && !isAssigned && chore.status === 'open';

  /**
   * A job somebody is booked on with no street address. Only possible for
   * chores posted before 16 September 2026, when the address stopped being
   * optional - but those exist, and "check the address above" over an empty
   * space is exactly what the owner reported. So each side is told what to do
   * about it instead.
   */
  const involved = isOwner || isAssigned || isBackup;
  const addressMissing =
    involved && !chore.location.address && ['open', 'assigned'].includes(chore.status);

  return (
    <div className="container section">
      <BackLink to="/marketplace" label={t('choreDetail.backToMarketplace')} />

      {notice && <div className="alert alert-success" style={{ marginBottom: 'var(--space-sm)' }}>{notice}</div>}
      {error && <div className="alert alert-error" style={{ marginBottom: 'var(--space-sm)' }}>{error}</div>}

      <div className="sidebar-layout sidebar-right">
        {/* ---------------- MAIN COLUMN ---------------- */}
        <div className="stack" style={{ gap: 'var(--space-md)' }}>
          <div className="card">
            <div className="row" style={{ gap: 'var(--space-xs)', marginBottom: 'var(--space-xs)' }}>
              <StatusBadge status={chore.status} />
              <Badge tone="soft-blue" icon={Tag}>
                {chore.category}
              </Badge>
              {chore.distanceKm !== null && chore.distanceKm !== undefined && (
                <Badge tone="soft-amber" icon={MapPin}>
                  {travel(chore)}
                </Badge>
              )}
              {chore.needsBackup && isOwner && (
                <Badge tone="amber" icon={AlertTriangle}>
                  {t('choreDetail.noBackupChosen')}
                </Badge>
              )}
            </div>

            <h1 style={{ fontSize: 'var(--text-h2)', marginBottom: 'var(--space-xs)' }}>{chore.title}</h1>

            <div className="card-meta" style={{ marginBottom: 'var(--space-sm)' }}>
              <span>
                <Calendar size={15} aria-hidden="true" />
                {/* On a multi-day booking the single date would be a lie by
                    omission - it is the NEXT one of several. The full list is
                    below; this line says how many there are so nobody reads
                    one date and thinks that is the whole job. */}
                {chore.isMultiDay
                  ? `${chore.visitCount} visits, next ${choreWhenShort(chore)}`
                  : choreWhenShort(chore)}
                {chore.hoursUntilStart > 0 && ` (${countdown(chore.hoursUntilStart)})`}
              </span>
              <span>
                <Clock size={15} aria-hidden="true" />
                about {chore.estimatedHours} hour{chore.estimatedHours === 1 ? '' : 's'}
                {chore.isMultiDay && ' each time'}
              </span>
              <span>
                <MapPin size={15} aria-hidden="true" />
                {chore.location.city}
              </span>
            </div>

            {/* ---------------- EVERY DATE ---------------- */}
            {chore.isMultiDay && (
              <>
                <hr className="divider" />
                <h3>
                  {t('choreDetail.theDates')}
                  {chore.repeatSummary && (
                    <span className="text-sm text-muted"> - {chore.repeatSummary}</span>
                  )}
                </h3>
                <p className="text-sm" style={{ marginTop: 'var(--space-xs)' }}>
                  {t('choreDetail.eachTimeTotal', { each: money(chore.pay), total: money(chore.totalPay) })}
                </p>

                <ul className="visit-list" style={{ marginTop: 'var(--space-xs)' }}>
                  {chore.visits.map((visit) => {
                    const start = new Date(visit.start);
                    const isPast = start < new Date();
                    const cancelled = visit.status === 'cancelled';
                    // The 24-hour rule, asked the same way the server asks it.
                    // A poster is releasing the student rather than leaving
                    // them short, so it does not apply to them.
                    const tooLate = (start - Date.now()) / 3600000 < 24;
                    const canDrop =
                      !cancelled
                      && !isPast
                      && chore.visitCount > 1
                      && (isOwner || (isAssigned && !tooLate))
                      && !['completed', 'cancelled'].includes(chore.status);

                    return (
                      <li
                        key={visit._id}
                        className={`visit-row${cancelled ? ' is-cancelled' : ''}`}
                      >
                        <span className="visit-when">
                          {dateTime(visit.start)}
                          {cancelled && (
                            <span className="visit-note">{t('choreDetail.visitCancelled')}</span>
                          )}
                          {!cancelled && isPast && (
                            <span className="visit-past">{t('choreDetail.visitDone')}</span>
                          )}
                        </span>

                        {canDrop && (
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={() => setDroppingVisit(visit)}
                          >
                            {isOwner ? t('choreDetail.cancelThisDay') : t('choreDetail.cannotMakeDay')}
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>

                {isAssigned && (
                  <p className="hint" style={{ marginTop: 'var(--space-xs)' }}>
                    {t('choreDetail.dropSingleDay')}
                  </p>
                )}
              </>
            )}

            <hr className="divider" />

            <h3>{t('choreDetail.whatNeedsDoing')}</h3>
            <p style={{ whiteSpace: 'pre-line', marginTop: 'var(--space-xs)' }}>{chore.description}</p>

            {/* The street address only appears for the people who need it. */}
            {chore.location.address && (
              <>
                <hr className="divider" />
                <div className="alert alert-info">
                  <Home size={18} aria-hidden="true" />
                  <div>
                    <strong>{t('choreDetail.address')}</strong> {chore.location.address}
                        <br />
                    <span className="text-sm">
                      {t('choreDetail.sharedBecauseInvolved')}
                    </span>
                  </div>
                </div>
              </>
            )}

            {addressMissing && (
              <>
                <hr className="divider" />
                <div className="alert alert-warning">
                  <Home size={18} aria-hidden="true" />
                  <div className="text-sm">
                    {isOwner ? (
                      <>
                        {t('choreDetail.noAddressOwner')}{' '}
                        <Link to={`/chores/${chore._id}/edit`}>{t('choreDetail.addAddress')}</Link>
                      </>
                    ) : (
                      t('choreDetail.noAddressStudent', { name: poster?.firstName || '' })
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* ---------------- WHO IS DOING IT ---------------- */}
          {(chore.assignedStudent || chore.backupStudent) && (
            <div className="card">
              <h3 style={{ marginBottom: 'var(--space-sm)' }}>{t('choreDetail.whoIsDoing')}</h3>

              {chore.assignedStudent && (
                <div className="profile-card" style={{ marginBottom: 'var(--space-sm)' }}>
                  <Avatar person={chore.assignedStudent} />
                  <div className="profile-card-body">
                    <div className="row" style={{ gap: 'var(--space-xs)' }}>
                      <Link to={`/students/${chore.assignedStudent._id}`} className="profile-name">
                        {chore.assignedStudent.firstName} {chore.assignedStudent.lastName}
                      </Link>
                      <Badge tone="green">{t('choreDetail.doingThis')}</Badge>
                    </div>
                    <StarRating
                      value={chore.assignedStudent.rating?.average || 0}
                      count={chore.assignedStudent.rating?.count || 0}
                      size={14}
                    />
                  </div>
                </div>
              )}

              {chore.backupStudent && (
                <div className="profile-card">
                  <Avatar person={chore.backupStudent} />
                  <div className="profile-card-body">
                    <div className="row" style={{ gap: 'var(--space-xs)' }}>
                      <Link to={`/students/${chore.backupStudent._id}`} className="profile-name">
                        {chore.backupStudent.firstName} {chore.backupStudent.lastName}
                      </Link>
                      <Badge tone="soft-blue">{t('choreDetail.backupBadge')}</Badge>
                    </div>
                    <StarRating
                      value={chore.backupStudent.rating?.average || 0}
                      count={chore.backupStudent.rating?.count || 0}
                      size={14}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ---------------- REVIEWS ---------------- */}
          {chore.status === 'completed' && (
            <div className="card">
              <h3 style={{ marginBottom: 'var(--space-sm)' }}>{t('choreDetail.reviews')}</h3>

              {canReview && reviewSubject && (
                <div
                  className="card"
                  style={{ background: 'var(--color-bg-subtle)', marginBottom: 'var(--space-sm)' }}
                >
                  <h4 style={{ marginBottom: 'var(--space-xs)' }}>{t('choreDetail.yourTurn')}</h4>
                  <ReviewForm
                    choreId={chore._id}
                    subjectName={reviewSubject.firstName}
                    onDone={() => {
                      setNotice(t('choreDetail.reviewSaved'));
                      load();
                    }}
                  />
                </div>
              )}

              {reviews.length === 0 ? (
                <p className="text-muted">{t('choreDetail.noReviews')}</p>
              ) : (
                reviews.map((review) => <ReviewCard key={review._id} review={review} />)
              )}
            </div>
          )}
        </div>

        {/* ---------------- SIDEBAR ---------------- */}
        <div className="stack" style={{ gap: 'var(--space-md)' }}>
          <div className="card">
            {/* The big figure is what the whole job pays. On a single-day
                chore that is the pay; on a booking it is the total, because
                this panel is what somebody reads to answer "is this worth my
                time" - and against 26 evenings, "€15" answers a different
                question from the one being asked. The rate is directly
                underneath so neither number can stand in for the other. */}
            <span className="text-sm text-muted">{t('choreDetail.payment')}</span>
            <div className="pay-amount-large">
              {money(chore.isMultiDay ? chore.totalPay : chore.pay)}
            </div>
            <span className="text-sm text-muted">
              {chore.isMultiDay
                ? `${money(chore.pay)} for each of the ${chore.visitCount} visits`
                : `about ${money((chore.pay / (chore.estimatedHours || 1)).toFixed(2))} per hour`}
            </span>

            <hr className="divider" />

            {/* ---- What this viewer can do ---- */}
            {canApply && (
              <>
                <button type="button" className="btn btn-primary btn-block" onClick={() => setApplyOpen(true)}>
                  {takingBackups ? t('choreDetail.applyBackup') : t('choreDetail.apply')}
                </button>
                {takingBackups && (
                  <p className="hint" style={{ marginTop: 'var(--space-xs)' }}>
                    {t('choreDetail.takingBackups')}
                  </p>
                )}
              </>
            )}

            {isStudent && myApplication && myApplication.status !== 'withdrawn' && (
              <div className="stack" style={{ gap: 'var(--space-xs)' }}>
                <div className="row-between">
                  <span className="text-sm text-muted">{t('choreDetail.yourApplication')}</span>
                  <ApplicationBadge status={myApplication.status} />
                </div>

                {myApplication.status === 'pending' && (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm btn-block"
                    onClick={handleWithdraw}
                    disabled={busy}
                  >
                    {t('choreDetail.withdrawApplication')}
                  </button>
                )}

                {(myApplication.status === 'accepted' || myApplication.status === 'backup') &&
                  chore.status === 'assigned' && (
                    <>
                      {/* Somebody who agreed to a Saturday morning and is now
                          being asked about a Tuesday needs telling, in plain
                          words, that saying no is allowed and free. Left to
                          work it out, most sixteen-year-olds would either go
                          anyway or drop out and assume it counted. */}
                      {myApplication.status === 'accepted' && freeCancellation && (
                        <div className="alert alert-info">
                          <CalendarClock size={18} aria-hidden="true" />
                          <div>
                            <strong>{t('choreDetail.datesMovedTitle')}</strong>
                            <br />
                            <span className="text-sm">{t('choreDetail.datesMovedBody')}</span>
                          </div>
                        </div>
                      )}

                      {/* ---- LATE, BUT NOT REFUSED ----
                          This used to REPLACE the cancel button with "you
                          cannot cancel within 24 hours". That stopped being
                          true on 25 August 2026 - and while it was there, the
                          button was simply gone, so a student who could not go
                          had no way to say so from this screen at all.

                          Now it warns and the button stays. The dialog it
                          opens asks for the explanation. */}
                      {myApplication.status === 'accepted' && withinCancelWindow && (
                        <div className="alert alert-warning">
                          <AlertTriangle size={18} aria-hidden="true" />
                          <div>
                            <strong>{t('choreDetail.startsSoonTitle')}</strong>
                            <br />
                            <span className="text-sm">
                              {t('choreDetail.startsSoonBody', {
                                when: countdown(chore.hoursUntilStart),
                                name: poster.firstName,
                              })}
                            </span>
                          </div>
                        </div>
                      )}

                      <button
                        type="button"
                        className="btn btn-outline btn-sm btn-block"
                        onClick={() => setCancelOpen(true)}
                        disabled={busy}
                      >
                        {myApplication.status === 'backup' ? t('choreDetail.stepDown') : t('choreDetail.cancelJob')}
                      </button>
                    </>
                  )}
              </div>
            )}

            {/* Not while it is still taking backups - that message sat directly
                under an "Apply as backup" button and flatly contradicted it. */}
            {isStudent && chore.status !== 'open' && !takingBackups && !myApplication && (
              <p className="text-sm text-muted">{t('choreDetail.noLongerTaking')}</p>
            )}

            {isOwner && (
              <Link to={`/dashboard/chores/${chore._id}`} className="btn btn-primary btn-block">
                <Users size={18} /> Manage applications
                {chore.applicationCount > 0 && ` (${chore.applicationCount})`}
              </Link>
            )}

            {isPoster && !isOwner && (
              <p className="text-sm text-muted">
                {t('choreDetail.onlyStudentsApply')}
              </p>
            )}

            {!user && (
              <div className="stack" style={{ gap: 'var(--space-xs)' }}>
                <Link to="/signup/student" className="btn btn-primary btn-block">
                  {t('choreDetail.signUpToApply')}
                </Link>
                <Link to="/login" className="btn btn-ghost btn-sm btn-block">
                  {t('choreDetail.alreadyHaveAccount')}
                </Link>
              </div>
            )}

            {(isAssigned || isBackup) && chore.status === 'assigned' && (
              <div className="alert alert-success" style={{ marginTop: 'var(--space-sm)' }}>
                <CheckCircle2 size={18} aria-hidden="true" />
                <div className="text-sm">
                  {isBackup
                    ? t('choreDetail.youAreBackup')
                    : chore.location.address
                      ? t('choreDetail.jobIsYours')
                      : t('choreDetail.jobIsYoursNoAddress', { name: poster?.firstName || '' })}
                </div>
              </div>
            )}

            {canMessage && (
              <Link
                to={`/messages/${chore._id}`}
                className="btn btn-outline btn-block chore-message-btn"
              >
                <MessageSquare size={18} aria-hidden="true" />
                {t('choreDetail.messageName', { name: messageName || '' })}
              </Link>
            )}

            {/* Under the Apply button, not beside it: the question is the
                smaller step and the page should not present the two as equal
                choices. */}
            {canAskAboutJob && (
              <Link
                to={`/messages/${chore._id}`}
                className="btn btn-outline btn-block chore-message-btn"
              >
                <HelpCircle size={18} aria-hidden="true" />
                {t('choreDetail.askAboutJob')}
              </Link>
            )}
          </div>

          {/* ---- Who posted it ---- */}
          {poster && (
            <div className="card">
              <span className="text-sm text-muted">{t('choreDetail.postedBy')}</span>
              <div className="profile-card" style={{ marginTop: 'var(--space-xs)' }}>
                <Avatar person={poster} />
                <div className="profile-card-body">
                  <Link to={`/users/${poster._id}`} className="profile-name">
                    {poster.firstName} {poster.lastName}
                  </Link>
                  <div>
                    <StarRating
                      value={poster.rating?.average || 0}
                      count={poster.rating?.count || 0}
                      size={14}
                    />
                  </div>
                  {poster.location?.city && (
                    <span className="text-sm text-muted">
                      <MapPin size={13} style={{ display: 'inline', verticalAlign: -2 }} />{' '}
                      {poster.location.city}
                    </span>
                  )}
                </div>
              </div>

              {!isOwner && (
                <>
                  <hr className="divider" />
                  <ReportButton
                    aboutUser={poster._id}
                    aboutChore={chore._id}
                    personName={poster.firstName}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ---------------- APPLY POP-UP ---------------- */}
      <Modal
        open={applyOpen}
        onClose={() => setApplyOpen(false)}
        title={`Apply for "${chore.title}"`}
        actions={
          <>
            <button type="button" className="btn btn-ghost btn-inline" onClick={() => setApplyOpen(false)}>
              {t('common.cancel')}
            </button>
            <button type="button" className="btn btn-primary btn-inline" onClick={handleApply} disabled={busy}>
              {busy ? t('choreDetail.sending') : t('choreDetail.sendApplication')}
            </button>
          </>
        }
      >
        <div className="field">
          <label htmlFor="apply-message">{t('choreDetail.addMessage')}</label>
          <textarea
            id="apply-message"
            value={applyMessage}
            onChange={(event) => setApplyMessage(event.target.value)}
            maxLength={500}
            placeholder={t('choreDetail.applyPlaceholder')}
          />
          <span className="hint">{applyMessage.length}/500</span>
        </div>
        <p className="text-sm text-muted">
          {poster?.firstName} will see your profile, your rating and this message.
        </p>
      </Modal>

      {/* ---------------- DROP ONE DAY POP-UP ---------------- */}
      <Modal
        open={Boolean(droppingVisit)}
        onClose={() => setDroppingVisit(null)}
        title={t('choreDetail.cancelDayQ')}
        actions={
          <>
            <button
              type="button"
              className="btn btn-ghost btn-inline"
              onClick={() => setDroppingVisit(null)}
            >
              {t('choreDetail.keepIt')}
            </button>
            <button
              type="button"
              className="btn btn-danger btn-inline"
              onClick={handleDropVisit}
              disabled={busy}
            >
              {busy ? t('choreDetail.cancelling') : t('choreDetail.yesCancelDay')}
            </button>
          </>
        }
      >
        {/* Naming the date rather than saying "this visit" - somebody
            confirming a cancellation should be able to check they are
            cancelling the day they meant. */}
        <p>
          <strong>{droppingVisit && dateTime(droppingVisit.start)}</strong> will be cancelled.
          The other {(chore.visitCount || 1) - 1}{' '}
          {(chore.visitCount || 1) - 1 === 1 ? 'day stays' : 'days stay'} exactly as they are, and
          the whole booking carries on.
        </p>
        <div className="field" style={{ marginTop: 'var(--space-sm)' }}>
          <label htmlFor="dropReason">{t('choreDetail.whyOptional')}</label>
          <input
            id="dropReason"
            type="text"
            maxLength={300}
            value={dropReason}
            onChange={(e) => setDropReason(e.target.value)}
            placeholder={isOwner ? t('choreDetail.whyPosterPlaceholder') : t('choreDetail.whyStudentPlaceholder')}
          />
          <span className="hint">
            {isOwner
              ? t('choreDetail.whyToStudent')
              : t('choreDetail.whyToPoster')}
          </span>
        </div>
      </Modal>

      {/* ---------------- CANCEL POP-UP ---------------- */}
      <Modal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title={myApplication?.status === 'backup' ? t('choreDetail.stepDownQ') : t('choreDetail.cancelJobQ')}
        actions={
          <>
            <button type="button" className="btn btn-ghost btn-inline" onClick={() => setCancelOpen(false)}>
              {t('choreDetail.keepIt')}
            </button>
            <button
              type="button"
              className="btn btn-danger btn-inline"
              onClick={handleCancelJob}
              disabled={busy || (needsCancelReason && cancelReasonTooShort)}
            >
              {busy ? t('choreDetail.cancelling') : t('choreDetail.yesCancel')}
            </button>
          </>
        }
      >
        <p>
          {myApplication?.status === 'backup'
            ? t('choreDetail.removedBackup')
            : t('choreDetail.removedJob')}
        </p>
        {myApplication?.status === 'accepted' && (
          <div className="alert alert-warning" style={{ marginTop: 'var(--space-sm)' }}>
            <AlertTriangle size={18} aria-hidden="true" />
            <div className="text-sm">
              {t('choreDetail.cancellingShows')}
            </div>
          </div>
        )}

        {/* ---- LATE: SAY WHY ----
            Inside 24 hours a student CAN now cancel, and has to explain. The
            box only appears when it is needed, because asking everybody for a
            reason would turn an ordinary week-ahead cancellation into an
            interrogation. */}
        {needsCancelReason && (
          <div className="field" style={{ marginTop: 'var(--space-sm)' }}>
            <label htmlFor="cancel-reason">{t('choreDetail.whyCancelling')} *</label>
            <p className="hint" style={{ marginTop: 0 }}>
              {t('choreDetail.whyCancellingHint', { name: poster?.firstName || '' })}
            </p>
            <textarea
              id="cancel-reason"
              rows={3}
              value={cancelReason}
              onChange={(event) => setCancelReason(event.target.value)}
              maxLength={1000}
              placeholder={t('choreDetail.whyCancellingPlaceholder')}
              aria-invalid={cancelReasonTooShort}
            />
            {cancelReasonTooShort && (
              <span className="hint">{t('choreDetail.whyCancellingShort')}</span>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
